import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import { fetchBomDetail } from '#/api/momMgmt/settings/bom/fetchDetail';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { dialogWideMaxWidthClassName } from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { SelectCore } from '#/modules/commonComponents/form/select';
import {
  bomGroupSelectionKey,
  expandBomToItems,
} from '#/modules/crmMgmt/costEstimate/helper/costEstimateLines';
import { formatCostEstimateDecimal } from '#/modules/crmMgmt/costEstimate/helper/costEstimateFormat';
import { parseDetailAssemblyOptions } from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';
import { bomDetailQueryKey, detailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function collectMtrIds(bom) {
  const ids = [];
  (bom?.groups ?? []).forEach((group) => {
    (group.mtrs ?? []).forEach((material) => {
      ids.push(material.mtrId ?? material.mtr?.id);
    });
  });
  (bom?.extraMtrs ?? []).forEach((extra) => ids.push(extra.mtrId ?? extra.mtr?.id));
  return [...new Set(ids.filter(Boolean))];
}

function describeBom(bom) {
  const status = bom.status === 'INACTIVE' ? '停用' : '啟用';
  return `${bom.name ?? ''}｜版本 ${bom.version ?? '-'}｜${status}。`
    + '數量依 BOM 原數量加入（無套數倍率），同料會累加到既有明細。';
}

/**
 * BOM 展開確認：列出 BOM 代號／版本與物料料號、名稱、數量；GROUP 料須選一個 ACTIVE 一般物料。
 * 確認後回傳展開後的物料（同料已加總），由編輯頁併入明細；不顯示試算總額。
 */
const CostEstimateBomExpandDialog = ({ open, onOpenChange, bomId, onConfirm }) => {
  const queryClient = useQueryClient();
  const [selections, setSelections] = useState({});
  const [isConfirming, setIsConfirming] = useState(false);

  const bomQuery = useQuery({
    queryKey: bomDetailQueryKey(bomId),
    queryFn: () => fetchBomDetail(bomId),
    enabled: Boolean(bomId) && open,
  });
  const bom = bomQuery.data?.data;
  const mtrIds = useMemo(() => collectMtrIds(bom), [bom]);

  const mtrQueries = useQueries({
    queries: mtrIds.map((mtrId) => ({
      queryKey: detailQueryKey('mtr', mtrId),
      queryFn: () => fetchMtrDetail(mtrId),
      enabled: open,
    })),
  });
  const mtrsReady = mtrQueries.every((query) => !query.isLoading);
  const mtrById = useMemo(() => {
    const map = new Map();
    mtrQueries.forEach((query, index) => {
      if (query.data?.data) {
        map.set(mtrIds[index], query.data.data);
      }
    });
    return map;
  }, [mtrQueries, mtrIds]);

  const rows = useMemo(() => {
    const result = [];
    (bom?.groups ?? []).forEach((group) => {
      (group.mtrs ?? []).forEach((material) => {
        const mtrId = material.mtrId ?? material.mtr?.id;
        const detail = mtrById.get(mtrId);
        const isGroup = detail?.mtrKind === 'GROUP';
        result.push({
          key: bomGroupSelectionKey(group.displayOrder, material.displayOrder),
          groupName: group.name ?? '',
          mtrNo: material.mtr?.mtrNo ?? detail?.mtrNo ?? '',
          name: material.mtr?.name ?? detail?.name ?? '',
          qty: material.qty,
          isGroup,
          options: isGroup
            ? parseDetailAssemblyOptions(detail)
              .filter((option) => !option.status || option.status === 'ACTIVE')
              .map((option) => ({
                value: option.optionMtrId,
                label: [option.mtrNo, option.name].filter(Boolean).join(' ') || option.optionMtrId,
              }))
            : [],
        });
      });
    });
    (bom?.extraMtrs ?? []).forEach((extra, index) => {
      result.push({
        key: `extra:${index}`,
        groupName: '其他',
        mtrNo: extra.mtr?.mtrNo ?? '',
        name: extra.mtr?.name ?? '',
        qty: extra.qty,
        isGroup: false,
        options: [],
      });
    });
    return result;
  }, [bom, mtrById]);

  async function handleConfirm() {
    const { items, missingSelections, invalidQty } = expandBomToItems(bom, { mtrById, selections });
    if (missingSelections.length) {
      toast.error(`請先為替代料選擇一般物料：${missingSelections.join('、')}`);
      return;
    }
    if (invalidQty.length) {
      toast.error(`數量超過四位小數，整次不匯入：${invalidQty.join('、')}`);
      return;
    }
    // GROUP 選定的子料不一定在 BOM 內，補讀 detail 帶出單位與建議價
    const missingIds = items.map((item) => item.mtrId).filter((mtrId) => !mtrById.has(mtrId));
    setIsConfirming(true);
    try {
      const fetched = await Promise.all(missingIds.map((mtrId) => queryClient.fetchQuery({
        queryKey: detailQueryKey('mtr', mtrId),
        queryFn: () => fetchMtrDetail(mtrId),
      })));
      const fullMap = new Map(mtrById);
      fetched.forEach((result, index) => fullMap.set(missingIds[index], result?.data ?? {}));
      onConfirm(expandBomToItems(bom, { mtrById: fullMap, selections }).items, bom);
      onOpenChange(false);
    } catch {
      toast.error('讀取替代料資料失敗，請稍後再試');
    } finally {
      setIsConfirming(false);
    }
  }

  const isLoading = bomQuery.isLoading || !mtrsReady;
  const title = bom ? `從 BOM 加入：${bom.code ?? ''}` : '從 BOM 加入';

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={bom ? describeBom(bom) : undefined}
      maxWidthClassName={dialogWideMaxWidthClassName}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={isConfirming ? '處理中…' : '確認加入'}
      cancelText="取消"
      confirmDisabled={isLoading || isConfirming || !bom}
    >
      {isLoading ? <p className="text-sm text-muted-foreground">載入 BOM…</p> : null}
      {bomQuery.isError ? <p className="text-sm text-destructive">載入 BOM 失敗。</p> : null}
      {!isLoading && bom ? (
        <div className="max-h-[60vh] overflow-auto rounded border border-slate-200">
          <table className="w-full min-w-[40rem] text-left text-sm">
            <thead className="sticky top-0 bg-slate-100 text-xs text-gray-600">
              <tr>
                <th className="px-2 py-1.5 font-medium">群組</th>
                <th className="px-2 py-1.5 font-medium">料號</th>
                <th className="px-2 py-1.5 font-medium">名稱</th>
                <th className="px-2 py-1.5 text-right font-medium">數量</th>
                <th className="px-2 py-1.5 font-medium">替代料選擇</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((row) => (
                <tr key={row.key} className={Number(row.qty) > 0 ? '' : 'text-gray-400'}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.groupName || '-'}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{row.mtrNo || '-'}</td>
                  <td className="px-2 py-1.5">{row.name || '-'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatCostEstimateDecimal(row.qty)}</td>
                  <td className="min-w-56 px-2 py-1.5">
                    {row.isGroup && Number(row.qty) > 0 ? (
                      <SelectCore
                        id={`bomSelection-${row.key}`}
                        options={row.options}
                        value={selections[row.key] ?? ''}
                        onChange={(value) => setSelections((prev) => ({ ...prev, [row.key]: value }))}
                        noOptionsMessage="此替代料無可用一般物料"
                        aria-label={`替代料 ${row.mtrNo}`}
                        className="w-full"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {Number(row.qty) > 0 ? '—' : '未使用（不計入）'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </AppDialog>
  );
};

CostEstimateBomExpandDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  bomId: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
};

export default CostEstimateBomExpandDialog;
